// Intent: owner wants "mikro famfary i konfetti" on the FIRST contribution to
// a goal — a small dopamine hit that builds the saving habit. Pure reanimated
// (already a dependency), ~1.6 s, auto-finishes; no third-party confetti libs.

import { useEffect, useState } from 'react';
import { Dimensions, View } from 'react-native';
import Animated, {
  Easing,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  type SharedValue,
} from 'react-native-reanimated';

const COLORS = ['#208AEF', '#0B2C4D', '#7BE3A2', '#FFC94D', '#FF7A7A', '#7CC4F0'];
const PIECES = 18;
const DURATION_MS = 1600;

interface PieceSpec {
  x0: number;
  drift: number;
  fall: number;
  delay: number;
  rotate: number;
  color: string;
  size: number;
}

function makePieces(width: number): PieceSpec[] {
  return Array.from({ length: PIECES }, (_, i) => ({
    x0: (width / PIECES) * i + Math.random() * 16,
    drift: (Math.random() - 0.5) * 90,
    fall: 320 + Math.random() * 260,
    delay: Math.random() * 250,
    rotate: (Math.random() - 0.5) * 720,
    color: COLORS[i % COLORS.length],
    size: 7 + Math.random() * 5,
  }));
}

function Piece({ spec, progress }: { spec: PieceSpec; progress: SharedValue<number> }) {
  const style = useAnimatedStyle(() => {
    const p = Math.min(1, Math.max(0, progress.value - spec.delay / DURATION_MS) * (1 + spec.delay / DURATION_MS));
    return {
      transform: [
        { translateX: spec.x0 + spec.drift * p },
        { translateY: -20 + spec.fall * p },
        { rotate: `${spec.rotate * p}deg` },
      ],
      opacity: p < 0.75 ? 1 : 1 - (p - 0.75) / 0.25,
    };
  });
  return (
    <Animated.View
      style={[
        {
          position: 'absolute',
          width: spec.size,
          height: spec.size * 1.6,
          borderRadius: 2,
          backgroundColor: spec.color,
        },
        style,
      ]}
    />
  );
}

/** One-shot confetti overlay; renders nothing after the burst completes. */
export function ConfettiBurst({ onDone }: { onDone: () => void }) {
  const progress = useSharedValue(0);
  // Lazy state keeps the random layout stable across parent re-renders.
  const [pieces] = useState(() => makePieces(Dimensions.get('window').width));

  useEffect(() => {
    progress.value = withTiming(1, { duration: DURATION_MS, easing: Easing.out(Easing.quad) }, (finished) => {
      if (finished) runOnJS(onDone)();
    });
    // fire once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <View pointerEvents="none" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 100 }}>
      {pieces.map((spec, i) => (
        <Piece key={i} spec={spec} progress={progress} />
      ))}
    </View>
  );
}
